<template>
  <section class="sticker patternmaker-patternmaker-view-thumbnails">
    <div v-if="enableSwiper" class="patternmaker-patternmaker-view-thumbnails-wrapper">
      <swiper
        @touchEnd="stickerTouched"
        @slideChange="stickerSlideChanged"
        :options="swiperOptions"
        :class="['patternmaker-patternmaker-view-thumbnails-wrapper-swiper', {
          'patternmaker-patternmaker-view-thumbnails-wrapper-swiper--no-loop': !swiperOptions.loop
        }]"
        ref="swiper"
        :auto-update="false"
        :auto-destroy="false"
        :delete-instance-on-destroy="false"
        :cleanup-styles-on-destroy="false"
      >
        <swiper-slide
          v-for="(item, index) in stickers"
          :class="[
            'patternmaker-patternmaker-view-item',
            {
              active: item.id === currentSticker,
              disabled: !enabled
            }
          ]"
          :data-sticker-id="item.id"
          :key="index"
        >
          <div class="icon-check" v-show="item.id === currentSticker"></div>
          <img class="background"
            :src="item.thumb"
            :alt="item.display_name.default"
            :data-sticker-id="item.id"
            :data-sticker-group-name="item.originalGroup"
          />
        </swiper-slide>
      </swiper>
      <div :class="['patternmaker-patternmaker-view-thumbnails-wrapper-nav-wrapper', { 'hide': !enableCarouselNavigation }]">
        <button type="button" class="patternmaker-patternmaker-view-thumbnails-wrapper-nav-button-previous stickers">
          <span class="icon-arrow-left-indicator-open" slot="button-prev"></span>
        </button>
        <button type="button" class="patternmaker-patternmaker-view-thumbnails-wrapper-nav-button-next stickers">
          <span class="icon-arrow-right-indicator-open" slot="button-next"></span>
        </button>
      </div>
    </div>
    <base-spinner v-else size="50" position="inherit"></base-spinner>
  </section>
</template>

<script>
import baseSpinner from '@/components/Spinner';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';

const swiperOptions = {
  slidesPerView: 4.5,
  slidesPerGroup: 1,
  loop: false,
  loopedSlides: 0,
  speed: 300,
  freeMode: true,
  freeModeMomentum: true,
  freeModeMomentumRatio: 0.9,
  breakpoints: {
    375: {
      slidesPerView: 5.5,
    },
    768: {
      slidesPerView: 7,
      freeModeMomentumRatio: 0.75,
    },
  },
  shortSwipes: false,
  watchSlidesProgress: true,
  watchSlidesVisibility: true
};

export default {
  name: 'patternmakerStickersCarousel',
  components: {
    baseSpinner,
    Swiper,
    SwiperSlide
  },
  props: {
    stickers: {
      type: Array,
      default: () => []
    },
    currentSticker: {
      type: [ Number, String ],
      default: null
    },
    enabled: {
      type: Boolean,
      default: false
    },
    slideToSelectedItem: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      enableCarouselNavigation: true,
      enableSwiper: false,
      swiperOptions: {
        ...swiperOptions,
        navigation: {
          prevEl: '.patternmaker-patternmaker-view-thumbnails-wrapper-nav-button-previous.stickers',
          nextEl: '.patternmaker-patternmaker-view-thumbnails-wrapper-nav-button-next.stickers'
        }
      },
      currentStickerActiveIndex: -1,
      isClicked: false
    }
  },
  computed: {
    swiper() {
      return this.$refs.swiper.$swiper;
    },
    enableStickerGroups() {
      return this.$store.state.utils.configuration.enableStickerGroups;
    }
  },
  watch: {
    stickers() {
      this.checkEnableCarouselNavigation();
    },
  },
  methods: {
    checkEnableLoop() {
      let enableLoop;
      const viewportWidth = window.innerWidth;
      const breakpoints = this.swiperOptions.breakpoints ? Object.keys(this.swiperOptions.breakpoints).sort((a, b) => Number(a)-Number(b)) : [];
      if (!breakpoints.length || viewportWidth < Number(breakpoints[0])) {
        enableLoop = this.stickers.length > this.swiperOptions.slidesPerView;
      }
      for (let i = 0; i < breakpoints.length - 1 && !enableLoop; i++) {
        if (viewportWidth >= Number(breakpoints[i]) && viewportWidth < Number(breakpoints[i + 1])) {
          enableLoop = this.stickers.length > this.swiperOptions.breakpoints[breakpoints[i]].slidesPerView;
        }
      }
      if (!enableLoop && breakpoints.length && viewportWidth >= Number(breakpoints[breakpoints.length - 1])) {
        enableLoop = this.stickers.length > this.swiperOptions.breakpoints[breakpoints[breakpoints.length - 1]].slidesPerView;
      }
      return enableLoop;
    },
    checkEnableCarouselNavigation() {
      if (this.stickers && !this.stickers.length) return;
      const enableLoop = this.checkEnableLoop();
      const viewportWidth = window.innerWidth;
      if (this.swiperOptions.loop !== enableLoop) this.swiperOptions.loop = enableLoop;
      if (enableLoop && (this.swiperOptions.loopedSlides !== this.stickers.length)) this.swiperOptions.loopedSlides = this.stickers.length;
      this.enableCarouselNavigation = enableLoop && viewportWidth >= 768;
      this.enableSwiper = !!this.stickers.length;
    },
    /**
     * @function stickerTouched
     * If the slide is disable it prevents clicks again until the image is loaded(enabled=true)
     * The limits are -1 to 1 to catch the click event.
     * Otherwise the user is swiping the images.
     */
    stickerTouched(event){
      if (this.isClicked || !this.enabled) return;
      this.isClicked = true;
      const diff = this.swiper.touches.startX - this.swiper.touches.currentX;
      if (diff >= -1 && diff <= 1) {
        this.stickerClicked(event);
      }
      this.isClicked = false;
    },
    stickerClicked(e) {
      const id = e.target.getAttribute('data-sticker-id');
      const groupName = e.target.getAttribute('data-sticker-group-name');
      let sticker;
      if (groupName) {
        sticker = this.stickers.find(sticker => Number(sticker.id) === Number(id) && sticker.originalGroup === groupName);
      } else {
        sticker = this.stickers.find(sticker => Number(sticker.id) === Number(id));
      }
      if (this.$store.getters['utils/useEcomDataLayerFormat']) {
        this.$patternMakerDatalayer.logAnalyticsEvent({action: 'Select Pattern Sticker', eventLabel: sticker.id});
      }
      if (this.enableStickerGroups) {
        const stickerIndex = this.stickers.indexOf(sticker);
        this.updateStickerTabComponent(sticker, stickerIndex);
      }

      this.$emit('sticker-selected', sticker);

      let eventAction = 'pattern maker';
      if (this.$store.getters['patternMaker/isSecondComponentFlow']) {
       eventAction += ' - second component';
      }
    },
    goToSticker({ groupName, stickerIndex = null, moveToFirstStickerOfGroup = false }) {
      if (!this.swiperOptions.loop || !moveToFirstStickerOfGroup) return;
      let originalArrayIndex;
      if (stickerIndex !== null) {
        originalArrayIndex = stickerIndex;
      } else {
        // find the first sticker of the group
        const firstStickerIndex = this.stickers.findIndex(sticker => sticker.originalGroup === groupName);
        if (firstStickerIndex !== -1) {
          originalArrayIndex = firstStickerIndex;
        }
      }

      const activeIndex = this.swiper.activeIndex;

      const totalOriginalElements = this.stickers.length;
      const totalLoopedSlides = this.swiper.loopedSlides || 0;
      let lowerPosition = originalArrayIndex + totalLoopedSlides - totalOriginalElements;
      let upperPosition = originalArrayIndex + totalLoopedSlides;
      if (lowerPosition < 0) { // advance to next 'window'
        lowerPosition += totalOriginalElements;
        upperPosition += totalOriginalElements;
      }
      const distanceToLowerPosition = Math.abs(activeIndex - lowerPosition);
      const distanceToUpperPosition = Math.abs(activeIndex - upperPosition);
      let position;
      // finds the closest position
      if (distanceToLowerPosition < distanceToUpperPosition && lowerPosition >= 0) {
        position = lowerPosition;
      } else {
        position = upperPosition;
      }
      // first try to use prev/next to avoid the jump caused by a sequence of calls to slideTo
      if (position === activeIndex - 1) {
        this.swiper.slidePrev();
      } else if (position === activeIndex + 1) {
        this.swiper.slideNext();
      } else {
        const slidedWithSuccess = this.swiper.slideTo(position, this.swiperOptions.speed, false);
        if (!slidedWithSuccess && position !== activeIndex) { // go back one 'window'
          position -= totalOriginalElements;
          this.swiper.slideTo(position, this.swiperOptions.speed, false);
        }
      }
      this.currentStickerActiveIndex = position;
    },
    stickerSlideChanged() {
      if (!this.enableStickerGroups || !this.swiperOptions.loop) {
        return;
      }

      const { realIndex, activeIndex } = this.swiper;
      const sticker = this.stickers[realIndex];

      if (this.currentStickerActiveIndex !== activeIndex && sticker) {
        const group = this.getStickerGroup(sticker, false);
        this.$eventBus.$emit('sticker-tab-selected', group);
        this.currentStickerActiveIndex = activeIndex;
      }
    },
    getStickerGroup({ alternativeGroup, originalGroup }, getAlternativeGroup) {
      return alternativeGroup && getAlternativeGroup ? alternativeGroup : originalGroup;
    },
    /**
     * @function moveCurrentStickerIntoView
     * When the stickers are loaded and there's already a selected sticker it tries to move it into view
     */
    moveCurrentStickerIntoView() {
      const currentSticker = this.currentSticker ? this.stickers.find(sticker => sticker.id === this.currentSticker && sticker.originalGroup !== 'new') : null;
      const currentStickerIndex = this.stickers.indexOf(currentSticker);
      if (currentStickerIndex < 0) return;
      else this.$nextTick(() => {
        this.swiper.slideToLoop(currentStickerIndex);
        if (this.enableStickerGroups) this.updateStickerTabComponent(currentSticker, currentStickerIndex);
      });
    },
    updateStickerTabComponent(currentSticker, currentStickerIndex) {
      const groupName = this.getStickerGroup(currentSticker, true);

      this.$eventBus.$emit('sticker-tab-selected', groupName);
      this.goToSticker({ groupName: groupName, stickerIndex: currentStickerIndex, moveToFirstStickerOfGroup: this.slideToSelectedItem });
    },
  },
  created() {
    this.checkEnableCarouselNavigation();
    this.$eventBus.$on('patternmaker-go-to-sticker', this.goToSticker);
    this.$eventBus.$on('patternmaker-move-current-sticker-into-view', this.moveCurrentStickerIntoView);
    window.addEventListener('resize', this.checkEnableCarouselNavigation);
  },
  mounted() {
    if (this.stickers.length && this.currentSticker && this.slideToSelectedItem) this.moveCurrentStickerIntoView();
  },
  beforeDestroy() {
    this.$eventBus.$off('patternmaker-go-to-sticker', this.goToSticker);
    this.$eventBus.$off('patternmaker-move-current-sticker-into-view', this.moveCurrentStickerIntoView);
    window.removeEventListener('resize', this.checkEnableCarouselNavigation);
  }
}
</script>
